import React from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import LayersIcon from '@mui/icons-material/Layers';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useAuth0 } from '@auth0/auth0-react';
import { AutoBulkFiltersContext } from '../contexts/AutoBulkFiltersContext';
import { MachinesContext } from '../../../modules/machinesAdminManagement/contexts/MachinesContext';
import ConfirmCancelModal from '../../../modules/shared/modal/ConfirmCancelModal';
import AutocompleteFilter from '../../../modules/shared/form/AutocompleteFilter';
import DateSelectFilter from '../../../modules/shared/form/DateSelectFilter';
import FormInput from '../../../modules/shared/form/FormInput';
import MachineAutocompleteFilter from '../../../modules/shared/form/MachineAutocompleteFilter';
import SelectInput from '../../../modules/shared/form/SelectInput';
import { SnackBarContext } from '../../../modules/snackBar/contexts/SnackBarContext';

const GROUP_OPTIONS = ['Customer', 'Product', 'Both'] as const;
const DATE_OPTIONS = ['Today', 'Yesterday', 'This Week', 'Last Week', 'This Month', 'Last Month', 'Custom'] as const;
type GroupOption = typeof GROUP_OPTIONS[number];
type DateRangeOption = typeof DATE_OPTIONS[number];
type ExportMode = 'ALL' | 'FILTERS' | 'GROUP_BY';

interface Props {
  applyFilter?: (filters: string) => void;
  displayUOM?: string;
  filterChoices?: Record<string, string[]>;
  filters?: { label: string; options?: any[]; optionSelector: string; pluralLabel: string; selector?: string }[];
  filterType?: string;
  filterValues?: any;
  setDisplayUOM?: any;
  setFilterValues?: any;
  exportTransactions: (...args: any[]) => Promise<any>;
  includeProducts: boolean;
  setIncludeProducts: (val: boolean) => void;
  transactionMetrics?: { total_records?: number };
}

export default function TransactionsFilterBar({
  applyFilter,
  displayUOM,
  filterChoices,
  filters = [],
  filterType = '',
  filterValues,
  setDisplayUOM,
  setFilterValues,
  exportTransactions,
  includeProducts,
  setIncludeProducts,
  transactionMetrics,
}: Props) {
  const { user } = useAuth0();
  const { showErrorSnackBar, showSuccessSnackBar } = React.useContext(SnackBarContext);
  const { convertFilterValuesToUrlParams, fetchFilterOptions, filterOptionsLoading } =
    React.useContext(AutoBulkFiltersContext);
  const { autoBulkMachines, machinesLoading } = React.useContext(MachinesContext);

  const [exportMode, setExportMode] = React.useState<ExportMode>('FILTERS');
  const [destEmail, setDestEmail] = React.useState(user?.email || '');
  const [includeHeader, setIncludeHeader] = React.useState(true);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [machines, setMachines] = React.useState<string[]>(
    JSON.parse(localStorage.getItem('autoBulkMachines') || '[]'),
  );
  const [temporaryValues, setTemporaryValues] = React.useState<Record<string, any>>(filterValues);
  const [groupBy, setGroupBy] = React.useState<GroupOption>('Customer');
  const [dateRange, setDateRange] = React.useState<DateRangeOption>('Today');
  const [groupStartDate, setGroupStartDate] = React.useState(localStorage.getItem('groupStartDate') || '');
  const [groupEndDate, setGroupEndDate] = React.useState(localStorage.getItem('groupEndDate') || '');

  React.useEffect(() => {
    setDestEmail(user?.email || '');
  }, [user]);

  React.useEffect(() => {
    localStorage.setItem('autoBulkMachines', JSON.stringify(machines));
    fetchFilterOptions();
  }, [machines]);

  React.useEffect(() => {
    if (dateRange !== 'Custom') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      let s = '';
      let e = '';
      switch (dateRange) {
        case 'Today':
          s = today.toISOString().slice(0, 10);
          e = now.toISOString().slice(0, 10);
          break;
        case 'Yesterday': {
          const y = new Date(today);
          y.setDate(y.getDate() - 1);
          s = y.toISOString().slice(0, 10);
          e = y.toISOString().slice(0, 10);
          break;
        }
        case 'This Week': {
          const diff = (today.getDay() + 6) % 7;
          const st = new Date(today);
          st.setDate(st.getDate() - diff);
          s = st.toISOString().slice(0, 10);
          e = now.toISOString().slice(0, 10);
          break;
        }
        case 'Last Week': {
          const diff = (today.getDay() + 6) % 7;
          const st = new Date(today);
          st.setDate(st.getDate() - diff - 7);
          const ed = new Date(st);
          ed.setDate(st.getDate() + 6);
          s = st.toISOString().slice(0, 10);
          e = ed.toISOString().slice(0, 10);
          break;
        }
        case 'This Month': {
          const st = new Date(now.getFullYear(), now.getMonth(), 1);
          s = st.toISOString().slice(0, 10);
          e = now.toISOString().slice(0, 10);
          break;
        }
        case 'Last Month': {
          const st = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const ed = new Date(now.getFullYear(), now.getMonth(), 0);
          s = st.toISOString().slice(0, 10);
          e = ed.toISOString().slice(0, 10);
          break;
        }
      }
      setGroupStartDate(s);
      setGroupEndDate(e);
    }
  }, [dateRange]);

  const getTotalRecords = () => transactionMetrics?.total_records ?? '';

  const handleApplyFilters = () => {
    const params = convertFilterValuesToUrlParams?.(machines, temporaryValues) || '';
    setFilterValues?.(temporaryValues);
    applyFilter?.(params);
  };

  const handleExport = () => {
    const localTime = new Date().toString();
    const utcOffset = new Date().getTimezoneOffset();
    const filterParams = convertFilterValuesToUrlParams?.(machines, temporaryValues);
    setFilterValues?.(filterParams);

    let promise;
    if (exportMode === 'ALL') {
      promise = exportTransactions(destEmail, includeHeader, includeProducts, localTime, utcOffset);
    } else if (exportMode === 'FILTERS') {
      promise = exportTransactions(
        destEmail,
        includeHeader,
        includeProducts,
        localTime,
        utcOffset,
        filterParams,
        filterType,
      );
    } else {
      // exportGroupedTransactions
      promise = exportTransactions(
        destEmail,
        includeHeader,
        includeProducts,
        localTime,
        utcOffset,
        filterParams,
        filterType,
        { mode: 'GROUP_BY', groupBy, dateRange, startDate: groupStartDate, endDate: groupEndDate },
      );
    }

    promise
      .then(() => {
        showSuccessSnackBar(
          `Your export is now being processed. Please check your inbox for your ${filterType} export.`,
        );
        setModalOpen(false);
      })
      .catch((err) => {
        setModalOpen(false);
        showErrorSnackBar(`There was an issue: ${err}`);
      });
  };

  return (
    <Grid container spacing={1} alignItems="flex-start">
      <ConfirmCancelModal
        isOpen={modalOpen}
        onCancel={() => setModalOpen(false)}
        onConfirm={handleExport}
        title={`Export ${filterType}`}
        hasFooterBar
        height="600px"
        confirmLabel={`Export ${getTotalRecords()} ${filterType}`}
      >
        <Box m={2}>
          <Typography variant="h6">Select An Export Filter Category:</Typography>
          <ToggleButtonGroup
            exclusive
            value={exportMode}
            onChange={(_, val) => val && setExportMode(val)}
            sx={{ mb: 2 }}
          >
            <ToggleButton
              style={{ margin: '10px', marginLeft: 0, borderRadius: '8px', border: '1px solid #ccc' }}
              value="ALL"
            >
              <StorageIcon fontSize="small" />
              <Typography ml={1} style={{ textTransform: 'capitalize' }}>
                All Transactions
              </Typography>
            </ToggleButton>
            <ToggleButton style={{ margin: '10px', borderRadius: '8px', border: '1px solid #ccc' }} value="FILTERS">
              <FilterAltIcon fontSize="small" />
              <Typography ml={1} style={{ textTransform: 'capitalize' }}>
                Current Filters
              </Typography>
            </ToggleButton>
            <ToggleButton style={{ margin: '10px', borderRadius: '8px', border: '1px solid #ccc' }} value="GROUP_BY">
              <LayersIcon fontSize="small" />
              <Typography ml={1} style={{ textTransform: 'capitalize' }}>
                Grouped Export
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>

          {(exportMode === 'FILTERS' || exportMode === 'ALL') && (
            <Box>
              <Typography>Include filter values in header?</Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={includeHeader.toString()}
                  onChange={(e) => setIncludeHeader(e.target.value === 'true')}
                >
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>

              {filterType === 'Transactions' && (
                <>
                  <Typography mt={2}>Include nested product data?</Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={includeProducts.toString()}
                      onChange={(e) => setIncludeProducts(e.target.value === 'true')}
                    >
                      <FormControlLabel value="true" control={<Radio />} label="Yes, separate sheet" />
                      <FormControlLabel value="false" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>
                </>
              )}
            </Box>
          )}

          {exportMode === 'GROUP_BY' && (
            <Box>
              <Typography>Select Filter to Group by:</Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  name="group-by"
                  value={groupBy}
                  onChange={(_, value) => setGroupBy(value as GroupOption)}
                >
                  {GROUP_OPTIONS.map((opt) => (
                    <FormControlLabel key={opt} value={opt} control={<Radio size="small" />} label={opt} />
                  ))}
                </RadioGroup>
              </FormControl>

              <Typography mt={2}>Include filter values in header?</Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={includeHeader.toString()}
                  onChange={(e) => setIncludeHeader(e.target.value === 'true')}
                >
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>

              {filterType === 'Transactions' && (
                <>
                  <Typography mt={2}>Include nested product data?</Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={includeProducts.toString()}
                      onChange={(e) => setIncludeProducts(e.target.value === 'true')}
                    >
                      <FormControlLabel value="true" control={<Radio />} label="Yes, separate sheet" />
                      <FormControlLabel value="false" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>
                </>
              )}

              <br />
              <Typography style={{ marginTop: 15 }}>Date range</Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <TextField select value={dateRange} onChange={(e) => setDateRange(e.target.value as DateRangeOption)}>
                  {DATE_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>

              {dateRange === 'Custom' ? (
                <Box display="flex" gap={2}>
                  <TextField
                    label="Start date"
                    type="date"
                    value={groupStartDate}
                    onChange={(e) => setGroupStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="End date"
                    type="date"
                    value={groupEndDate}
                    onChange={(e) => setGroupEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '10px',
                      p: 1,
                      width: '100%',
                      backgroundColor: 'rgba(25, 118, 210, 0.06)',
                      border: '1px solid rgba(25, 118, 210, 0.2)',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#1976d2',
                      }}
                    >
                      {groupStartDate === groupEndDate ? (
                        <span>{groupStartDate}</span>
                      ) : (
                        <span>
                          {groupStartDate} - {groupEndDate}
                        </span>
                      )}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          <br />
          <Typography mt={2}>Destination Email Addresses</Typography>
          <FormInput value={destEmail} onChange={(val) => setDestEmail(val)} label="" />
        </Box>
      </ConfirmCancelModal>

      <Grid item xs={12}>
        <Grid container spacing={1} alignItems="flex-start">
          <Grid item>
            <DateSelectFilter />
          </Grid>
          <Grid item>
            <MachineAutocompleteFilter
              label="Machines"
              loading={machinesLoading}
              pluralLabel="Machines"
              choices={autoBulkMachines}
              setValue={(val) => setMachines([].concat(val))}
              value={machines}
            />
          </Grid>
          {filterType === 'Transactions' && (
            <Grid item xs={12} md={2}>
              <FormInput
                value={temporaryValues?.work_order_name || ''}
                onChange={(val) => setTemporaryValues({ ...temporaryValues, work_order_name: val })}
                label="Order Name"
              />
            </Grid>
          )}
          {filters.map((f) => (
            <Grid item key={f.label}>
              <AutocompleteFilter
                label={f.label}
                loading={filterOptionsLoading}
                pluralLabel={f.pluralLabel}
                choices={f.options || filterChoices?.[f.optionSelector] || []}
                setValue={(val) => setTemporaryValues({ ...temporaryValues, [f.selector || f.optionSelector]: val })}
                value={temporaryValues[f.selector || f.optionSelector] || []}
              />
            </Grid>
          ))}
          {displayUOM && (
            <Grid item>
              <SelectInput
                label="Display UOM"
                value={displayUOM}
                onChange={(val) => setDisplayUOM?.(val)}
                choices={
                  temporaryValues?.product_type_list?.[0] === 'Liquid'
                    ? [
                        { label: 'Pounds', value: 'lbs' },
                        { label: 'Tons', value: 'tons' },
                        { label: 'Gallons', value: 'gallons' },
                        { label: 'Fl Oz', value: 'fl oz' },
                      ]
                    : [
                        { label: 'Pounds', value: 'lbs' },
                        { label: 'Tons', value: 'tons' },
                      ]
                }
              />
            </Grid>
          )}
          <Grid item>
            <Button variant="contained" style={{ marginTop: '19px' }} onClick={handleApplyFilters}>
              Apply
            </Button>
            <Button
              variant="contained"
              style={{ marginTop: '19px', marginLeft: '7px' }}
              onClick={() => setModalOpen(true)}
            >
              Export
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
